import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { environment } from '../../../environments/environment';

/**
 * Slide-over drawer for managing the domains allowed to embed a project's
 * feedback widget (the round's `allowedDomains`). Presentational: it edits a
 * local working copy seeded from the `domains` input and emits the full list via
 * `save`; the host persists it (e.g. `FeedbackRoundService.updateAllowedDomains`).
 */
@Component({
  selector: 'app-embed-settings-drawer',
  imports: [MatIconModule],
  templateUrl: './embed-settings-drawer.html',
  styleUrl: './embed-settings-drawer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown.escape)': 'onClose()' },
})
export class EmbedSettingsDrawer {
  /** Whether the drawer is shown. */
  readonly open = input<boolean>(false);

  /** The round's current allowed domains; seeds the editor when opened. */
  readonly domains = input<string[]>([]);

  /** True while the host is persisting a save (disables the controls). */
  readonly saving = input<boolean>(false);

  /** The org + project the embed snippet targets (wired from the round). */
  readonly orgId = input<string>('');
  readonly projectId = input<string>('');

  /** Emitted with the full domain list when the user saves. */
  readonly save = output<string[]>();

  /** Emitted when the user closes the drawer (button, overlay, or Escape). */
  readonly close = output<void>();

  /** Working copy of the domain list being edited. */
  protected readonly entries = signal<string[]>([]);

  /** The in-progress "add domain" input value. */
  protected readonly draft = signal('');

  /** Brief "Copied!" feedback state after the copy button is used. */
  protected readonly copied = signal(false);

  /**
   * TEMPORARY embed snippet shown for copy/paste, wired with the round's org +
   * project (the two values the widget will key off). The widget bundle origin is
   * environment-driven (`widgetBaseUrl`): dev → resources.dev, staging/production →
   * resources.live. The loader script is still a placeholder and will be replaced
   * with the final embed code later.
   */
  protected readonly embedCode = computed(
    () => `<brafton-feedback org="${this.orgId()}" project="${this.projectId()}"></brafton-feedback>
<script>(function(b,r,a,f,t,o,n){
    t = document.createElement("script");t.type=n;t.src=r+"main.js";b.appendChild(t);
    o = document.createElement("link");o.type="text/css";o.rel="stylesheet";o.href=r+"styles.css";b.appendChild(o);
})(document.getElementsByTagName("head")[0], "${environment.widgetBaseUrl}", null,null,null,null,"text/javascript");
</script>`,
  );

  constructor() {
    // Re-seed the working copy from the input each time the drawer opens.
    effect(() => {
      if (this.open()) {
        const seed = this.domains();
        untracked(() => {
          this.entries.set([...seed]);
          this.draft.set('');
        });
      }
    });
  }

  /** Adds the current draft as a domain (trimmed, case-insensitively unique). */
  protected addDomain(): void {
    const value = this.draft().trim();
    if (!value) {
      return;
    }
    const alreadyPresent = this.entries().some(
      (existing) => existing.toLowerCase() === value.toLowerCase(),
    );
    if (!alreadyPresent) {
      this.entries.update((current) => [...current, value]);
    }
    this.draft.set('');
  }

  protected removeDomain(domain: string): void {
    this.entries.update((current) => current.filter((entry) => entry !== domain));
  }

  /** Copies the embed snippet to the clipboard, with brief "Copied!" feedback. */
  protected async copyEmbedCode(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.embedCode());
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 1500);
    } catch {
      // Clipboard unavailable (insecure context / denied) — leave state as-is.
    }
  }

  protected onSave(): void {
    if (!this.saving()) {
      this.save.emit(this.entries());
    }
  }

  protected onClose(): void {
    if (!this.saving()) {
      this.close.emit();
    }
  }
}
